import { Box, Button, FormControl, FormLabel, Input, Stack } from '@chakra-ui/react';
import { ResistomapButton } from '@resistapp/client/components/buttons/resistomap-button';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { FormView } from '@resistapp/client/components/shared/layout';
import { ErrorDiv } from '@resistapp/client/components/states/error';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { usePasswordReset } from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { OtherParams } from '@resistapp/client/utils/url-manipulation';
import { useState } from 'react';

export function PasswordResetView() {
  const { getQueryParams } = useSearchParamsContext();
  const code = getQueryParams(OtherParams.VERIFICATION_CODE);
  const { create } = usePasswordReset();
  const { setLoggedIn } = useUser();
  const navigate = useNavigateWithQuery();
  const showToast = useToastWithoutDebounce();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const onSave = () => {
    if (!password || password !== confirm || !code) {
      setError('Passwords must match');
      return;
    }

    // Clear any previous validation error once we start a new request
    setError(undefined);

    create.mutate(
      { passwordResetCode: code, password },
      {
        onSuccess: user => {
          try {
            setLoggedIn(user);
            navigate('/', false);
          } catch (err) {
            // An unexpected error occurred while updating the app state - still notify the user.
            console.error('Failed to login after password reset', err);
            showToast({ title: 'Password reset, please login', status: 'success' });
          }
        },
        onError: (e: Error) => {
          showToast({ title: `Failed to reset password: ${e.message}`, status: 'error' });
        },
      },
    );
  };

  return (
    <FormView>
      <RSecondTitle>Reset password</RSecondTitle>
      <Stack spacing={5} width="full">
        <FormControl variant="floating" isRequired>
          <Input
            value={password}
            onChange={e => {
              setPassword(e.target.value);
            }}
            size="md"
            isRequired
            width="full"
            name="password"
            placeholder=" "
            type={showPassword ? 'text' : 'password'}
            autoFocus
          />
          <FormLabel bg="transparent">New password</FormLabel>
        </FormControl>
        <FormControl variant="floating" isRequired>
          <Input
            value={confirm}
            onChange={e => {
              setConfirm(e.target.value);
            }}
            size="md"
            isRequired
            width="full"
            name="confirm"
            placeholder=" "
            type={showPassword ? 'text' : 'password'}
          />
          <FormLabel bg="transparent">Confirm password</FormLabel>
        </FormControl>
        <Box width="full" textAlign="left" transform="translate(-10px,-20px)">
          <Button
            variant="ghost"
            bg="transparent"
            size="sm"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            onClick={() => {
              setShowPassword(prev => !prev);
            }}
          >
            {showPassword ? 'Hide password' : 'Show password'}
          </Button>
        </Box>
        <ResistomapButton
          onClick={() => {
            onSave();
          }}
          isDisabled={!password || !confirm || create.isPending}
          style={{ width: '100%' }}
        >
          Save password
        </ResistomapButton>
        {error && <ErrorDiv>{error}</ErrorDiv>}
      </Stack>
    </FormView>
  );
}
